import {
  can,
  canAnywhere,
  PERMISSIONS,
  type Actor,
  type ModuleKey,
  type Permission,
} from '@hrms/shared';

/**
 * A feature switch an organisation's admin can turn off (Travel Desk, ADR 0030). Distinct from
 * `module` below, which is the SUBSCRIPTION entitlement (`enabledModules` on the session). An entry
 * that names a switch is shown only while it is ON — on top of its `permission`, never instead of
 * it. The answer comes from the API, so `visibleNav` takes it as an argument (see `useNavSwitches`).
 * `group_member` is not an admin's switch but a fact about the person: they belong to a company
 * group they may see (docs/adr/0034-company-groups.md) — the owner, or someone the owner gave
 * group-wide training — which is who may see Admin > LMS > All companies.
 */
export type NavSwitch = 'travel' | 'merit_payroll_apply' | 'group_member';

/** Who may open the admin Employee List and Org Tree: anyone who reads, adds or edits employees. */
export const EMPLOYEE_LIST_PERMISSIONS = [
  'user:read',
  'employee:create',
  'employee:update',
] as const satisfies readonly Permission[];

/** Which switches are on. A switch absent from the object is treated as OFF. */
export type NavSwitchFlags = Partial<Record<NavSwitch, boolean>>;

export interface NavChild {
  to: string;
  label: string;
  /** Hidden while this switch is off for the organisation; see `NavSwitch`. */
  switch?: NavSwitch;
  /** `soon` items render greyed with a "Coming soon" badge and are not clickable — showing the
   * full 5-section Performance IA now is for discoverability, not for shipping links to
   * routes that don't exist yet. Flip to `live` in that section's own PR once its route lands. */
  status?: 'live' | 'soon';
  /** Same meaning as `NavItem.permission` — a child hidden unless the actor holds this permission
   * at any scope (e.g. Admin > Organisation's sub-pages). Absent means visible to anyone who can
   * already see the parent. Performance's other children are deliberately `status`-only (every
   * role that can see the Performance tab can use them); Insights is the one exception — it has no
   * employee-facing view at all (see its plan's role table), so it carries a real permission here
   * instead of dead-ending an employee into a "no access" page after they click it. */
  permission?: Permission | readonly Permission[];
  /** See `NavItem.module` — set only when this child belongs to a DIFFERENT module than its
   * siblings (e.g. Time's Shifts child is `shift_rostering`, not `attendance`). Absent means it
   * inherits visibility purely from its parent/permission, same as before B-P5. */
  module?: ModuleKey;
  /** See `NavItem.orgWide`. */
  orgWide?: boolean;
  /** Same as `NavSubItem.section`: a heading the flyout shows above the first visible child of a
   * run of children sharing it (Admin > Payroll's Overview / Setup / People / Run / Compliance). */
  section?: string;
  badge?: string | number;
  badgeTone?: 'neutral' | 'warning' | 'accent' | 'danger' | 'success' | 'amber' | 'blue';
  subLinks?: readonly {
    to: string;
    label: string;
    permission?: Permission | readonly Permission[];
  }[];
  /** Present only on a child that itself fans out into a pill-tab sub-shell one level deeper — the
   * child's own route renders a shell that switches between these tabs rather than being a single
   * page (Asset's Administration → Configuration/Vendors is the one user today). */
  tabs?: readonly { to: string; label: string; permission?: Permission | readonly Permission[] }[];
}

export interface NavSubItem {
  /** Appended as a path segment onto the parent item's `to` — its own route, its own page. */
  path: string;
  label: string;
  permission?: Permission | readonly Permission[];
  /**
   * Heading the flyout shows above the first VISIBLE item of a run of items sharing it. Purely
   * presentational: it changes no route, permission or path. Items without one belong to the
   * flyout's default (unheaded) group, which must come first — a heading applies to the items
   * that follow it, so a headed group is listed after the unheaded one.
   */
  section?: string;
}

/** The Attendance flyout's groups — see `NavSubItem.section`. `TEAM` is generic enough that other
 * modules (Exit, Projects & Timesheets below) reuse it for their own "do this for my reports"
 * group rather than minting a same-meaning constant per module. */
const TEAM = 'Team';
const SHIFT_MANAGEMENT = 'Shift Management';
/** Projects & Timesheets flyout's own "Reports" group (Analytics, Timesheet Register) — these
 * stay exclusively on this flyout, never duplicated into Admin > Projects (see that entry's own
 * doc comment for why). */
const PROJECTS_REPORTS = 'Reports';

export interface NavItem {
  to: string;
  label: string;
  /** Hidden while this switch is off for the organisation; see `NavSwitch`. */
  switch?: NavSwitch;
  /**
   * Absent means everyone signed in sees it. Present means the item renders only for an actor
   * holding this permission at ANY scope — `canAnywhere`, which exists for exactly this and is
   * documented as unsuitable for anything else. Hiding a tab is a courtesy, not a control: the
   * route behind it is still guarded, and the API behind that is the real boundary.
   */
  permission?: Permission | readonly Permission[];
  /**
   * B-P5 (KEKA_VS_BILLING.md §4): the module this section belongs to. Absent means every module
   * (org-level sections like Home/Inbox/Organisation) — never gated on a switch. Checked the same
   * way `permission` is: a courtesy hide, not the real boundary (the API-level
   * `requireModuleEntitlement` gate is). Only top-level sections carry this — a child inherits its
   * parent's module implicitly by the parent disappearing, so children are not tagged individually
   * unless they belong to a DIFFERENT module than their siblings (e.g. Time's Shifts vs Leave Admin).
   */
  module?: ModuleKey;
  /**
   * When true, `permission` must be held for the WHOLE ORGANISATION (`can` against the org), not
   * merely somewhere (`canAnywhere`). For a permission every employee holds at `self` scope —
   * `workflow:read` — "anywhere" would show the item to everyone.
   */
  orgWide?: boolean;
  /** Rendered as a hover/focus flyout in AppShell (`PerformanceNavItem`, generic despite the name —
   * dispatches for ANY item with children, not just Performance) rather than a flat link. Flat
   * `NavChild[]`, not recursively `NavItem[]` — nothing in this app nests a flyout inside a flyout,
   * and `PerformanceNavItem` renders each child as a plain link, never recursing into a child's own
   * `children`. */
  children?: NavChild[];
  /**
   * When present, the sidebar renders this item as a flyout trigger instead of a plain link — one
   * page (AttendancePage) with several submodules, rather than a route per submodule. Grows as
   * more submodules ship; each entry here must have a matching case in AttendancePage's tab switch.
   */
  subItems?: readonly NavSubItem[];
  /** Renders a header row atop this item's flyout — the label on the left, "Subsections" on the
   * right — ONLY when set. Every existing item leaves this undefined, so nothing else changes;
   * see `PerformanceNavItem` in AppShell.tsx. */
  submenuLabel?: string;
  /** Renders a small success-tone dot after the label on the flyout's currently-ACTIVE child row —
   * ONLY when true. Every existing item leaves this false/undefined; see `PerformanceNavItem`. */
  showActiveDot?: boolean;
}

/**
 * Asset's "Administration" child fans out one level deeper into these pill tabs, rather than being
 * its own single page — both `AdministrationShell` (the component rendering the pill-tab sub-shell)
 * and `breadcrumb.ts`'s `resolveBreadcrumb` read this same exported array, so it lives here once.
 * Currently 3 entries (Configuration, Vendors, Asset Batches); designed to grow to 4 (Insights)
 * without this shape changing — see the reconciliation report's canonical-structure decision.
 */
export const ASSET_ADMINISTRATION_TABS = [
  {
    to: '/asset-management/administration/configuration',
    label: 'Asset Configuration',
    permission: 'asset.category:read',
  },
  {
    to: '/asset-management/administration/vendors',
    label: 'Vendors',
    permission: 'asset.vendor:read',
  },
  {
    to: '/asset-management/administration/batches',
    label: 'Asset Batches',
    permission: 'asset.batch:read',
  },
  {
    to: '/asset-management/administration/history',
    label: 'Asset History & Audit',
    permission: 'asset:audit:read',
  },
] as const;

/**
 * Every permission that approves something. The Inbox's Approvals entry is shown to anyone holding
 * one of them, derived from the catalogue rather than listed, so a module that adds an approval
 * permission is covered without anyone remembering this line. Assigned approvers who hold none
 * (a specific-person step) still reach the same page through Inbox > Notifications, and the page
 * itself always lists their own approvals: the API gates /inbox on sign-in, not on a permission.
 */
export const APPROVER_PERMISSIONS: readonly Permission[] = PERMISSIONS.filter((p) =>
  p.endsWith(':approve'),
);

/**
 * THE MENU IS ORGANISED BY MODULE (owner decision D3): one entry per module, holding both the
 * person's own pages and — gated — their team's. There is no "Me" / "My Team" / "My Finances"
 * grouping. Configuration a module's administrators do lives in `ADMIN_NAV` under the same module
 * name. `status: 'soon'` marks a module page that does not exist yet: shown, never linked.
 */
export const NAV: readonly NavItem[] = [
  { to: '/', label: 'Home' },
  {
    to: '/inbox',
    label: 'Inbox',
    children: [
      { to: '/inbox', label: 'Approvals', permission: APPROVER_PERMISSIONS },
      { to: '/inbox?tab=notifications', label: 'Notifications' },
      // The full leave queue (decided history, batch approve, balances) that /approvals used to be.
      { to: '/leave/approvals', label: 'Leave Approval Queue', permission: 'leave:approve' },
    ],
  },
  {
    to: '/attendance',
    label: 'Attendance',
    module: 'attendance',
    subItems: [
      { path: 'my', label: 'My Attendance' },
      { path: 'status-report', label: 'My Status Report' },
      { path: 'my-roster', label: 'My Roster' },
      { path: 'requests', label: 'My Attendance Requests' },
      { path: 'shift-change-requests', label: 'My Shift Change Requests' },
      { path: 'on-duty-requests', label: 'My On Duty / Remote Work' },
      { path: 'early-out', label: 'My Early Out' },
      { path: 'late-in', label: 'My Late In' },

      // ---- Team -------------------------------------------------------------------------------
      // What a reporting manager does for their own reports. HR's org-wide screens (command
      // center, verification, shift definitions, penalty policies) live in Admin > Time.
      {
        path: 'employee',
        label: 'Employee Attendance',
        section: TEAM,
        permission: ['attendance:update', 'attendance:approve', 'shift:manage'],
      },
      {
        path: 'employee-status-report',
        label: 'Employee Status Report',
        section: TEAM,
        permission: ['attendance:update', 'attendance:approve', 'shift:manage'],
      },
      {
        path: 'employee-monthly',
        label: 'Employee Monthly Attendance',
        section: TEAM,
        permission: ['attendance:update', 'attendance:approve', 'shift:manage'],
      },
      {
        path: 'regularisations',
        label: 'Attendance Regularisations',
        section: TEAM,
        permission: [
          'attendance:approve',
          'attendance.regularisation:approve',
          'attendance:update',
        ],
      },
      {
        path: 'od-remote-requests',
        label: 'OD & Remote Work Requests',
        section: TEAM,
        permission: ['attendance:approve', 'attendance.od:approve', 'attendance:update'],
      },
      {
        path: 'shift-change-approvals',
        label: 'Shift Change Approvals',
        section: TEAM,
        // Matches AttendancePage's `canApproveShiftChange` — the API's team scope needs exactly this.
        permission: 'shift.change:approve',
      },
      {
        path: 'early-exit-waivers',
        label: 'Early-Exit Waiver Approvals',
        section: TEAM,
        permission: 'attendance.early_exit_waiver:approve',
      },
      {
        path: 'clock-out-policy',
        label: 'Clock-Out Policy',
        // Matches AttendancePage's `canManageClockOutPolicy` exactly (ADR 0019).
        permission: 'clock_out_policy:manage',
      },
      {
        // ATT-11: the physical biometric device (fingerprint/face scanner) registry — an inventory
        // list, not a vendor integration. Matches AttendancePage's `canManageBiometricDevices`
        // exactly, and the same permission every route in biometric-device.contract.ts requires —
        // HR admin and up, the same tier as Clock-Out Policy just above.
        path: 'biometric-devices',
        label: 'Biometric Devices',
        permission: 'biometric_device:manage',
      },
      {
        path: 'learning',
        // Training redesign §9.2: the older items' screen — new training is assigned from
        // Admin › Training › Courses; this page lists what is still open.
        label: 'Older learning items',
        // Matches AttendancePage's `canManageLearning` exactly — the same two permissions the
        // existing learning-assignments API has always gated on (ADR 0019).
        permission: ['learning:manage', 'learning.assign:execute'],
      },
      {
        path: 'team',
        label: 'Team Approvals',
        section: TEAM,
        // Matches AttendancePage's `canApproveTeam` exactly — the same predicate that gates the
        // route itself, so the sidebar link and the page agree on who it is for.
        permission: [
          'attendance:approve',
          'attendance.regularisation:approve',
          'shift.change:approve',
          'attendance.od:approve',
        ],
      },

      // ---- Shift Management -------------------------------------------------------------------
      // Assigning existing shifts to one's own reports. Shift DEFINITIONS are Admin > Time.
      {
        path: 'shift-assignments',
        label: 'Shift Assignments',
        section: SHIFT_MANAGEMENT,
        permission: ['shift:manage', 'attendance:update', 'attendance:approve'],
      },
      {
        path: 'shift-roster',
        label: 'Shift Roster',
        section: SHIFT_MANAGEMENT,
        permission: ['shift:manage', 'attendance:update', 'attendance:approve'],
      },
      // Shift Groups is the reusable rotation-cycle catalogue; createShiftGroup checks
      // shift.rotation:manage at ORG scope, so a manager who follows the link sees the list and a
      // create attempt correctly 403s — the same shape as every page gated deeper than its link.
      {
        path: 'shift-groups',
        label: 'Shift Groups',
        section: SHIFT_MANAGEMENT,
        permission: 'shift.rotation:manage',
      },
      // Putting specific employees onto a group's rotation IS person-scoped
      // (resolveRosterPersonScope), so a manager reaches their own reporting tree here.
      {
        path: 'rotation-assignments',
        label: 'Rotating Shift Assignments',
        section: SHIFT_MANAGEMENT,
        permission: 'shift.rotation:manage',
      },
    ],
  },
  {
    // Training redesign (PLAN §4.1, decision 8.4): the learner's home is /learning; every old
    // /training link redirects there.
    to: '/learning',
    label: 'My learning',
    module: 'training',
    permission: 'learning:read',
    // `children`, like Leave's "My Leave": the flyout must lead with the courses page itself.
    // (`subItems` is AttendancePage's tab mechanism and left only "Certificates" reachable.)
    children: [
      { to: '/learning', label: 'My courses' },
      { to: '/learning/certificates', label: 'Certificates' },
    ],
  },
  {
    to: '/leave',
    label: 'Leave',
    module: 'leave',
    // Approvals are not here any more: they are in the Inbox with every other module's.
    children: [
      { to: '/leave', label: 'My Leave' },
      // Both land on /leave itself (there's no separate route for either) — Leave.tsx reads these
      // query params on mount to open the request form / switch tabs, same page as My Leave.
      { to: '/leave?new=1', label: 'Request Leave' },
      { to: '/leave?tab=requests', label: 'My Requests' },
      { to: '/leave/calendar', label: 'Team Calendar' },
      { to: '/leave/holidays', label: 'Holiday Calendar' },
      { to: '/leave/policies', label: 'Leave Policies' },
    ],
  },
  {
    to: '/projects',
    label: 'Projects & Timesheets',
    module: 'projects',
    // Every PM page gets a real entry here now (previously only 7 of 22 were registered — the
    // rest only ever lived in ProjectsPage's own page-local pill bar). Gates below are copied
    // verbatim from ProjectsLayout.visibleTabs (ProjectsPage.tsx) so the sidebar and the page
    // can never disagree about who sees what.
    subItems: [
      { path: 'my', label: 'My Projects' },
      { path: 'tasks', label: 'My Tasks' },
      {
        path: 'timesheet',
        label: 'My Timesheet',
        permission: ['timesheet:submit', 'timesheet:read'],
      },
      { path: 'work-plan', label: "Today's Tasks" },
      { path: 'work-performance', label: 'Work Performance' },
      { path: 'utilization', label: 'My Utilization' },

      // ---- Team -------------------------------------------------------------------------------
      // What a reporting manager/approver does for their own reports. Org-wide admin screens
      // (All Projects, Work Forms, task configuration) are Admin > Projects.
      {
        path: 'next-day-plan',
        label: 'Next-Day Planning',
        section: TEAM,
        permission: 'project.task:manage',
      },
      {
        path: 'approvals',
        label: 'Timesheet Approvals',
        section: TEAM,
        permission: 'timesheet:approve',
      },
      {
        path: 'task-approvals',
        label: 'Task Approvals',
        section: TEAM,
        permission: 'work_plan:approve',
      },
      {
        path: 'extension-requests',
        label: 'Extension Requests',
        section: TEAM,
        permission: 'project.task:manage',
      },
      {
        path: 'resources',
        label: 'Resource Allocation',
        section: TEAM,
        permission: 'project.member:manage',
      },

      // ---- Reports ----------------------------------------------------------------------------
      {
        path: 'analytics',
        label: 'Analytics',
        section: PROJECTS_REPORTS,
        permission: 'project.analytics:read',
      },
      {
        path: 'register',
        label: 'Timesheet Register',
        section: PROJECTS_REPORTS,
        // Matches ProjectsPage's `canSeeRegister` exactly.
        permission: 'timesheet:approve',
      },

      // Org-wide admin screens (All Projects, Work Forms, task configuration, KPIs, Employee
      // Targets) live ONLY in Admin > Projects (ADMIN_NAV below) — never duplicated here too.
      // A manager/HR user who also holds admin rights reaches them from the Administration
      // sidebar, not from this everyday Core Modules flyout, so this list stays short for
      // everyone who opens it day to day.
    ],
  },
  {
    to: '/payslip',
    label: 'Payroll',
    module: 'statutory_payroll',
    children: [
      {
        to: '/payslip',
        label: 'Payslips',
        permission: ['payslip.own:read', 'payroll.payment.own:read'],
      },
      // The two below are routed under /admin/payroll, but they are self-service screens: each
      // is gated on an `.own` permission every employee holds, and shows HR its extra tabs itself.
      {
        to: '/admin/payroll/employee-pay/tax-declarations',
        label: 'Tax Declarations',
        permission: 'payroll.tax.own:manage',
      },
      { to: '/loans', label: 'Loans & Salary Advance', permission: 'payroll.loan:request' },
      {
        to: '/admin/payroll/compliance/salary-queries',
        label: 'Salary Queries',
        permission: 'payroll.query.own:manage',
      },
    ],
  },
  {
    to: '/claims',
    label: 'Expenses & Travel',
    module: 'expense_reimbursement',
    children: [
      // Advances and Expense reports used to be separate sidebar entries here, but both are just
      // `/claims` with a different tab pre-selected — the Claims page itself already surfaces them
      // as tabs (Approvals / My decisions / Advances / Reports / Travel / Settlement / Paid
      // history), so a second sidebar link to the same page was pure duplication. One entry now;
      // the tabs do the rest.
      { to: '/claims', label: 'Claims' },
      // Travel Desk (ADR 0030) — trips, their approvals and the traveller's own details. Every entry
      // vanishes while the organisation has the module off; the older Claims > Travel tab (R-P10)
      // stays reachable from the Claims screen while it is on.
      { to: '/travel', label: 'My trips', permission: 'travel:request', switch: 'travel' },
      {
        to: '/travel/approvals',
        label: 'Trip approvals',
        permission: 'travel:approve',
        switch: 'travel',
      },
      {
        // Phase 4 (TD-4.10): settlement rounds waiting on the caller, plus finance's net-off queue.
        // A traveller reaches their own rounds from the trip page instead.
        to: '/travel/money/settlements',
        label: 'Trip settlements',
        permission: ['travel:approve', 'expense:settle'],
        switch: 'travel',
      },
      {
        to: '/travel/profile',
        label: 'Traveller profile',
        permission: 'travel:request',
        switch: 'travel',
      },
      {
        to: '/travel/who-is-travelling',
        label: 'Who is travelling',
        permission: 'travel:read',
        switch: 'travel',
      },
      {
        to: '/travel/reports',
        label: 'Travel reports',
        permission: 'travel:read',
        switch: 'travel',
      },
      // R-P3 (Keka parity) — who a set of category limits applies to.
      {
        to: '/admin/reimbursement/policies',
        label: 'Expense policies',
        permission: 'expense.policy:read',
      },
    ],
  },
  {
    to: '/performance',
    label: 'Performance',
    module: 'performance_okr',
    children: [
      { to: '/performance', label: 'Dashboard', status: 'live' },
      { to: '/performance/goals', label: 'Goals & OKRs', status: 'live' },
      {
        to: '/performance/goals/team',
        label: 'Team Goals',
        // TeamGoals reads a manager's reports' goals; HR reaches everyone's through goal:manage.
        permission: ['goal:edit:team', 'goal:manage'],
      },
      { to: '/performance/feedback', label: 'Feedback & 1:1s', status: 'live' },
      { to: '/performance/reviews', label: 'Performance Reviews', status: 'live' },
      {
        // A manager's own merit worksheet (plan §5I step 5): a cycle picker that opens
        // /performance/reviews/merit/:cycleId. HR decides the round on the cycle's admin Merit tab.
        to: '/performance/reviews/merit',
        label: 'Merit Worksheet',
        permission: 'merit:submit',
      },
      {
        to: '/performance/improvement-plans',
        label: 'Improvement Plans',
        permission: 'improvement_plan:read',
      },
      {
        to: '/performance/insights',
        label: 'Performance Insights',
        status: 'live',
        permission: 'analytics.performance:read',
      },
    ],
  },
  {
    to: '/asset-management/my-assets',
    label: 'Assets',
    module: 'asset_management',
    showActiveDot: true,
    // The employee-facing asset pages. The register's administration is Admin > Assets.
    children: [
      { to: '/asset-management/my-assets', label: 'My Assets' },
      { to: '/asset-management/assets', label: 'Asset Inventory', permission: 'asset:read' },
      { to: '/asset-management/requests', label: 'Requests & Allocation' },
    ],
  },
  {
    to: '/my-details',
    label: 'Organisation',
    children: [
      { to: '/my-details', label: 'My Details' },
      { to: '/my-onboarding', label: 'My Onboarding', permission: 'onboarding:read' },
      // The directory and org chart are the employee-facing views; the admin Employee List and
      // Org Tree are HR screens (user:read).
      {
        to: '/organisation/directory',
        label: 'Employee Directory',
        permission: 'employee.directory:read',
      },
      { to: '/organisation/org-chart', label: 'Org Chart', permission: 'org_chart:read' },
      { to: '/organisation/policies', label: 'Policies', permission: 'policy:read' },
      { to: '/organisation/documents', label: 'Documents', permission: 'employee.document:read' },
    ],
  },
  {
    to: '/exit',
    label: 'Exit',
    module: 'offboarding',
    children: [
      { to: '/exit', label: 'My Exit' },
      { to: '/exit/interview', label: 'Exit Interview' },
      { to: '/exit/handover', label: 'My Handover' },
      // `offboarding.team:read` gates VISIBILITY of these two for managers browsing — the routes
      // themselves differ: /team/exits and its detail are wrapped in the matching
      // RequirePermission, but /team/exits/tasks is deliberately left unwrapped in App.tsx
      // because `listMyTasks` is `routeMeta(null)` (self-scoped) — IT/finance/HR task
      // assignees who hold no manager permission still need to land there from their own
      // `offboarding.task_*` notifications (see notifications/catalog.ts's hrefs).
      {
        to: '/team/exits',
        label: 'Team Exits',
        section: TEAM,
        permission: 'offboarding.team:read',
      },
      {
        to: '/team/exits/tasks',
        label: 'My Exit Tasks',
        section: TEAM,
        permission: 'offboarding.team:read',
      },
      // The default clearance owners: reporting managers, IT (asset:manage), finance and HR (fnf:read).
      // Anyone else assigned one by name reaches the page from its notification.
      {
        to: '/team/exits/clearances',
        label: 'My Exit Clearances',
        section: TEAM,
        permission: ['offboarding.team:read', 'asset:manage', 'fnf:read'],
      },
    ],
  },
  {
    to: '/engage/announcements',
    label: 'Engage',
    children: [
      { to: '/my-referrals', label: 'Refer a Friend', permission: 'referral:read' },
      // Anyone can be put on an interview panel; the list is their own (recruitment bug 11).
      { to: '/my-interviews', label: 'My Interviews' },
      // Open to every employee; the API checks they are a current one and the recruitment module is on.
      { to: '/jobs', label: 'Internal Jobs', module: 'ats_recruitment' },
      {
        to: '/engage/announcements',
        label: 'Announcements',
        permission: 'announcement:read',
        module: 'engage',
      },
      { to: '/engage/celebrations', label: 'Celebrations', module: 'engage' },
      {
        to: '/engage/recognition',
        label: 'Recognition',
        permission: 'recognition:give',
        module: 'recognition',
      },
      { to: '/engage/social', label: 'Social', module: 'chat' },
      { to: '/engage/surveys', label: 'Surveys', permission: 'survey:read', module: 'engage' },
    ],
  },
  // Every employee raises and follows their own tickets here (ADR 0028). Agents and Helpdesk
  // Managers work the queue from Admin > Help desk console.
  { to: '/helpdesk', label: 'Help desk', permission: 'ticket:read', module: 'helpdesk' },
];

/** Admin > Help desk console's configuration group. */
const HELPDESK_SETTINGS = 'Settings';

/** Admin > Payroll's groups — see `NavChild.section`. */
const PAYROLL_SETUP = 'Setup';
const PAYROLL_PEOPLE = 'People';
const PAYROLL_RUN = 'Run';
const PAYROLL_COMPLIANCE = 'Compliance & Reports';

/** Admin > Time's groups. */
const TIME_SHIFTS = 'Shifts';
const TIME_LEAVE = 'Leave Admin';
const TIME_REPORTS = 'Reports';

/** Who administers attendance org-wide — HR, not a reporting manager (see Attendance above). */
const ATTENDANCE_ADMIN: readonly Permission[] = ['attendance:update', 'shift:manage'];

/**
 * The admin area — one section per MODULE, mirroring `NAV`'s module names. A section with no
 * `permission` of its own is shown when at least one of its children is (visibleNav drops a
 * parent whose children all filtered out); each child carries the permission its page's route
 * checks. Read rather than manage wherever a read permission exists: an auditor holds `audit:read`
 * and nothing else and must still be able to reach the audit log.
 *
 * The narrow admin roles (ADR 0023) each find their section: Expense Manager → Expenses & Travel,
 * Performance Admin → Performance, Project Admin → Projects, Asset Manager → Assets.
 */
export const ADMIN_NAV: readonly NavItem[] = [
  {
    // Billing's old standalone "Organisation" section (Department/Branch/Company Profile/Post) is
    // dropped here — company main's "Organisation Setup" section below (line ~524) already
    // absorbed all four, plus Grades/Cost Centres/Business Units/Employee Groups/Exit Types/
    // Employee Fields/Policies. Keeping both would have duplicated four nav entries.
    to: '/admin/billing',
    label: 'Subscription & Billing',
    permission: 'org.billing:read',
    children: [
      { to: '/admin/billing/plans', label: 'Plans & Pricing', permission: 'org.billing:read' },
      { to: '/admin/billing/checkout', label: 'Checkout', permission: 'org.billing:read' },
      {
        to: '/admin/billing/invoices',
        label: 'Billing & Invoices',
        permission: 'org.billing:read',
      },
      { to: '/admin/billing/usage-seats', label: 'Usage & Seats', permission: 'org.billing:read' },
    ],
  },
  {
    // No parent `permission` — main's already-shipped Employees section relies on visibleNav's
    // "shown when at least one child is" rule (same pattern Recruitment uses above), not a change
    // billing needs to touch.
    to: '/admin/employee/list',
    label: 'Employees',
    children: [
      // Anyone who can add or edit employees needs the list (HR Executive holds employee:create
      // but not user:read — audit #4). The list itself shows each caller only their read scope.
      {
        to: '/admin/employee/list',
        label: 'Employee List',
        permission: EMPLOYEE_LIST_PERMISSIONS,
      },
      { to: '/admin/employee/tree', label: 'Org Tree', permission: EMPLOYEE_LIST_PERMISSIONS },
      {
        to: '/admin/employee/analytics',
        label: 'People Analytics',
        permission: EMPLOYEE_LIST_PERMISSIONS,
      },
      {
        to: '/admin/employee/profile-requests',
        label: 'Profile Requests',
        permission: 'employee:update',
      },
      // The old Command Center / Manager Action Desk / Buddy Desk / IT Provisioning / Onboarding
      // Check-ins entries were consolidated into the one page at /admin/onboarding itself — see
      // OnboardingProbationPage.tsx. The role-filtered work queue (which also carries the pulse
      // check-in results) is its own page beside it.
      {
        to: '/admin/onboarding',
        label: 'Onboarding & Probation',
        permission: 'onboarding:manage',
        module: 'onboarding',
      },
      {
        to: '/admin/onboarding/queue',
        label: 'Onboarding Work Queue',
        // HR-only in the menu (this module's scope). The route also admits IT and approvers by
        // URL, but an approver holding workflow.instance:approve — every reporting manager —
        // must not gain the whole Employees section from it; managers approve from the Inbox.
        permission: 'onboarding:manage',
      },
      {
        // Checklist templates for onboarding — NOT the approval flows (Settings > Approval
        // Workflows). Renamed from "Templates & Workflows" so the two are not confused.
        to: '/admin/onboarding/templates',
        label: 'Onboarding Templates',
        permission: 'onboarding:manage',
        module: 'onboarding',
      },
      {
        to: '/admin/employee/resignations',
        label: 'Exits',
        // What its API needs; user:read kept so nobody who has it today loses the page.
        permission: ['user:read', 'offboarding:read'],
      },
      // Full & Final settlement lives under Payroll > Run only (see PAYROLL_RUN section below) —
      // it used to be duplicated here too, pointing at the exact same route.
      {
        to: '/admin/employee/id-series',
        label: 'Employee ID series',
        permission: 'org.settings:read',
      },
    ],
  },
  {
    to: '/admin/organisation/company-profile',
    label: 'Organisation Setup',
    permission: 'org.settings:read',
    children: [
      // In the order a new organisation sets them up: the company, where people work, how they are
      // grouped, the levels, then the job titles (a designation picks a grade, so grades first).
      {
        to: '/admin/organisation/company-profile',
        label: 'Legal Entities',
        permission: 'org.settings:read',
      },
      { to: '/admin/organisation/branch', label: 'Locations', permission: 'org.settings:read' },
      {
        to: '/admin/organisation/department',
        label: 'Departments',
        permission: 'org.settings:read',
      },
      { to: '/admin/organisation/grades', label: 'Grades', permission: 'org.settings:read' },
      { to: '/admin/organisation/post', label: 'Designations', permission: 'org.settings:read' },
      {
        to: '/admin/organisation/probation-policies',
        label: 'Probation Policies',
        permission: 'org.settings:read',
      },
      {
        to: '/admin/organisation/cost-centres',
        label: 'Cost Centres',
        permission: 'org.settings:read',
      },
      {
        to: '/admin/organisation/business-units',
        label: 'Business Units',
        permission: 'org.settings:read',
      },
      {
        to: '/admin/organisation/employee-groups',
        label: 'Employee Groups',
        permission: 'org.settings:read',
      },
      {
        to: '/admin/organisation/exit-types',
        label: 'Exit Types',
        permission: 'org.settings:read',
      },
      {
        to: '/admin/organisation/employee-fields',
        label: 'Employee Fields',
        permission: 'org.settings:read',
      },
      {
        // Company policies (ADR 0026) are org configuration: the API checks policy:manage against
        // the whole organisation, so an entity-scoped grant does not reveal the row.
        to: '/admin/organisation/policies',
        label: 'Policies',
        permission: 'policy:manage',
        orgWide: true,
      },
    ],
  },
  {
    to: '/attendance/admin',
    label: 'Time',
    children: [
      {
        to: '/attendance/admin',
        label: 'Attendance Command Center',
        // Matches AttendancePage's `canManageAdmin` exactly.
        permission: ['attendance:update', 'shift:manage', 'org.settings:read'],
        module: 'attendance',
      },
      {
        to: '/attendance/employee',
        label: 'Employee Attendance',
        permission: ATTENDANCE_ADMIN,
        module: 'attendance',
      },
      {
        to: '/attendance/verification',
        label: 'Attendance Verification',
        permission: ATTENDANCE_ADMIN,
        module: 'attendance',
      },
      {
        to: '/attendance/absent-present',
        label: 'Employee Absent / Present',
        permission: ATTENDANCE_ADMIN,
        module: 'attendance',
      },
      {
        to: '/attendance/early-exit-policies',
        label: 'Early-Exit Penalty Policies',
        permission: 'attendance.penalty:manage',
        module: 'attendance',
      },
      {
        to: '/attendance/shifts',
        label: 'Shifts',
        section: TIME_SHIFTS,
        // Shift DEFINITIONS — the route gates on `canManageAdmin`.
        permission: ['shift:manage', 'attendance:update', 'org.settings:read'],
        module: 'shift_rostering',
      },
      {
        to: '/attendance/shift-roster',
        label: 'Shift Roster',
        section: TIME_SHIFTS,
        permission: ATTENDANCE_ADMIN,
        module: 'shift_rostering',
      },
      {
        to: '/leave/policies',
        label: 'Leave Policies',
        section: TIME_LEAVE,
        // The same page every employee reads; it shows editing only to this permission.
        permission: 'leave.policy:manage',
        module: 'leave',
      },
      {
        to: '/leave/holidays',
        label: 'Holidays',
        section: TIME_LEAVE,
        module: 'leave',
        permission: 'holiday_calendar:manage',
      },
      {
        to: '/admin/time/leave-balances',
        label: 'Leave Balances',
        section: TIME_LEAVE,
        permission: 'leave.balance:adjust',
      },
      {
        to: '/admin/time/leave-year-end',
        label: 'Leave Year-End',
        section: TIME_LEAVE,
        permission: 'leave.year_end:read',
        module: 'leave',
      },
      {
        to: '/admin/time/leave-plans',
        label: 'Leave Plans',
        section: TIME_LEAVE,
        // Seeing plans needs leave.plan:read; the page shows its edit actions only to leave.plan:manage.
        permission: 'leave.plan:read',
        module: 'leave',
      },
      {
        to: '/admin/time/leave-reports',
        label: 'Leave Reports',
        section: TIME_REPORTS,
        permission: 'leave.report:read',
        module: 'leave',
      },
      {
        to: '/attendance/employee-monthly',
        label: 'Employee Monthly Attendance',
        section: TIME_REPORTS,
        permission: ATTENDANCE_ADMIN,
      },
      {
        to: '/attendance/employee-status-report',
        label: 'Employee Status Report',
        section: TIME_REPORTS,
        permission: ATTENDANCE_ADMIN,
      },
      {
        to: '/attendance/muster-roll',
        label: 'Muster Roll',
        section: TIME_REPORTS,
        // A manager holds `attendance.team:read` for their team only. `orgWide` makes this check
        // `can(actor, 'attendance.team:read', { orgId })` instead of `canAnywhere`, matching exactly what
        // `attendance.getMusterRoll` itself requires (P1-19).
        permission: 'attendance.team:read',
        orgWide: true,
      },
    ],
  },
  {
    to: '/admin/training',
    // "LMS", not plain "Training": the employee menu has its own learning entry, and the two read as one page.
    label: 'LMS',
    module: 'training',
    // Training redesign R14: eight entries became four. Assign lives on each course; the monitor,
    // analytics, live attendance and the overrun queue are tabs of Reports. The old URLs redirect.
    children: [
      {
        to: '/admin/training/courses',
        label: 'Courses',
        permission: ['learning:manage', 'learning.assign:execute'],
      },
      {
        // Live sessions over Zoho Meeting or a pasted link; same pair trainingLiveSessions.list declares.
        to: '/admin/training/live',
        label: 'Live sessions',
        permission: ['learning:manage', 'learning.assign:execute'],
      },
      {
        // Also the overrun-approval queue's home (it was "Regularisations" here), so its approvers
        // keep the entry: Reports shows each of them only the tabs their permissions allow.
        to: '/admin/training/reports',
        label: 'Reports',
        permission: [
          'learning:manage',
          'learning.assign:execute',
          'attendance.regularisation:approve',
          'attendance:approve',
        ],
      },
      {
        // The person's group companies in one place (ADR 0036). Shown only to someone in a company
        // group (`group_member`), on top of the permission; the API checks both again.
        to: '/admin/training/all-companies',
        label: 'All companies',
        permission: 'learning.group:manage',
        switch: 'group_member',
      },
      {
        to: '/admin/training/settings',
        label: 'Settings',
        permission: 'learning:manage',
      },
    ],
  },
  {
    to: '/claims?tab=settlement',
    label: 'Expenses & Travel',
    module: 'expense_reimbursement',
    // Settling and setting policy, not approving: a reporting manager approves their team's claims
    // from the Inbox and from Claims > Team approvals, and has no admin section for it.
    permission: ['expense:settle', 'expense.policy:manage'],
    children: [
      // Claims / Settlement & Payment Runs / Paid History / Advances used to be four separate
      // entries here, but all four are the same `/claims` page with a different tab pre-selected
      // (Approvals, Settlement, Paid history, Advances are all tabs the page itself already shows)
      // — same duplication the employee-facing menu above had, and the same fix: one entry in.
      { to: '/claims?tab=approvals', label: 'Claims', permission: 'expense:approve' },
      // Travel Desk Phase 4 (TD-4.6 to 4.8): chasing what is left on advances — payroll recovery,
      // repayments — and proposing write-offs. Behind the Travel switch, like every travel page;
      // the org owner decides a write-off from the Inbox.
      {
        to: '/travel/money/advances',
        label: 'Outstanding advances',
        permission: 'expense.advance:manage',
        switch: 'travel',
      },
      {
        to: '/travel/money/write-offs',
        label: 'Advance write-offs',
        permission: 'expense.advance:manage',
        switch: 'travel',
      },
      {
        // The page narrows further to expense:read held wider than self; a reporting manager
        // reaches it by URL, since they have no admin section here.
        to: '/admin/expenses/reports',
        label: 'Dashboard & Reports',
        permission: ['expense:settle', 'expense:approve'],
      },
      {
        // Reimbursement categories and their limits.
        to: '/admin/reimbursement',
        label: 'Policies & Categories',
        permission: 'expense.policy:manage',
      },
    ],
  },
  {
    // Travel Desk (ADR 0030). The parent lands on /admin/travel, which sends the person to the first
    // page they may open (a desk agent, an expense manager and an HR executive reach different
    // ones). Settings stays visible while the module is off: it is where the switch lives.
    to: '/admin/travel',
    label: 'Travel Desk',
    children: [
      {
        to: '/travel/desk',
        label: 'Desk queue',
        permission: 'travel.desk:read',
        switch: 'travel',
      },
      {
        // The History screen carries the Airline credits tab; /travel/desk/credits is its own route.
        to: '/travel/desk/history',
        label: 'History & credits',
        permission: 'travel.desk:read',
        switch: 'travel',
      },
      {
        to: '/travel/desk/teams',
        label: 'Desk teams',
        permission: 'travel.desk:read',
        switch: 'travel',
      },
      {
        // listVendors' permission: agents read the list, `travel.desk:manage` changes it (EC-55).
        to: '/travel/desk/vendors',
        label: 'Desk vendors',
        permission: 'travel.desk:read',
        switch: 'travel',
      },
      {
        to: '/travel/desk/sla',
        label: 'Desk SLA',
        permission: 'travel.desk:manage',
        switch: 'travel',
      },
      {
        to: '/admin/travel/policies',
        label: 'Travel policies',
        permission: ['travel.policy:manage', 'travel:read'],
        switch: 'travel',
      },
      {
        to: '/admin/travel/city-tiers',
        label: 'City tiers',
        permission: ['travel.policy:manage', 'travel:read'],
        switch: 'travel',
      },
      {
        to: '/admin/travel/settings',
        label: 'Travel settings',
        permission: ['travel.policy:manage', 'travel.desk:manage'],
      },
    ],
  },
  {
    to: '/admin/offboarding',
    label: 'Offboarding',
    module: 'offboarding',
    // No single permission gates the whole section, matching Recruitment's precedent just above:
    // each child below carries its own routeMeta-matched permission (offboarding:read is
    // hr_manager+, offboarding:manage/offboarding.policy:manage are hr_admin-only), and they are
    // realistically held by different people.
    children: [
      { to: '/admin/offboarding', label: 'Dashboard', permission: 'offboarding:read' },
      {
        to: '/admin/offboarding/resignations',
        label: 'Resignations',
        permission: 'offboarding:read',
      },
      {
        to: '/admin/offboarding/pipeline',
        label: 'Exit Pipeline',
        permission: ['offboarding:read', 'offboarding.team:read'],
      },
      {
        to: '/admin/offboarding/fnf-settlements',
        label: 'Full & Final Settlement',
        permission: 'payroll:read',
      },
      { to: '/admin/offboarding/templates', label: 'Templates', permission: 'offboarding:manage' },
      {
        to: '/admin/offboarding/exit-interview-templates',
        label: 'Exit Interview Templates',
        permission: 'offboarding:manage',
      },
      {
        to: '/admin/offboarding/assets',
        label: 'Unreturned Assets',
        permission: ['offboarding:read', 'asset:manage'],
      },
      {
        to: '/admin/offboarding/settings',
        label: 'Settings',
        permission: 'offboarding.policy:manage',
      },
    ],
  },
  {
    to: '/admin/company-details',
    label: 'Company details',
    permission: 'org.settings:update',
  },
  {
    to: '/admin/agreements',
    label: 'Agreements',
    permission: 'agreement:read',
    children: [
      { to: '/admin/agreements', label: 'Statement of Facts', permission: 'agreement:read' },
    ],
  },
  {
    to: '/admin/payroll',
    label: 'Payroll',
    module: 'statutory_payroll',
    // Widened past `payroll:read` for exactly two children: a manager reads their team's payroll
    // cost and their reports' salary queries without holding payroll read. Every child below
    // carries its own permission, so widening the parent exposes nothing else.
    permission: ['payroll:read', 'payroll.report.team:read', 'payroll.query:read'],
    children: [
      // The module's landing screen, and the one child that carries the parent's full any-of list:
      // it is the entry point for everybody the parent admits, and it shows each of them only the
      // sections they hold something for (see PayrollDashboardPage's header).
      {
        to: '/admin/payroll/overview',
        label: 'Overview',
        permission: ['payroll:read', 'payroll.report.team:read', 'payroll.query:read'],
      },
      {
        to: '/admin/payroll/settings/company-statutory',
        label: 'Company & Statutory',
        section: PAYROLL_SETUP,
        permission: 'payroll:read',
      },
      {
        to: '/admin/payroll/settings/pay-calendar',
        label: 'Pay Calendar',
        section: PAYROLL_SETUP,
        permission: 'payroll:read',
      },
      {
        to: '/admin/payroll/settings/salary-configuration',
        label: 'Salary Configuration',
        section: PAYROLL_SETUP,
        permission: 'payroll:read',
      },
      {
        // The org-level switch for "apply merit to payroll" + its net-basis-arrears mode (plan
        // §27/§28). Not itself gated by the switch — this is where an admin turns it on.
        to: '/admin/payroll/settings/merit-payroll',
        label: 'Merit Auto-Apply',
        section: PAYROLL_SETUP,
        permission: 'compensation:update',
      },
      {
        to: '/admin/payroll/profiles-revisions',
        label: 'Profiles & Revisions',
        section: PAYROLL_PEOPLE,
        permission: 'payroll:read',
      },
      {
        // Approved merit increases waiting to be applied to pay (plan §5I step 9, D2). Gated by the
        // org's switch (plan §27) on top of the permission — off by default for every organisation.
        to: '/admin/payroll/merit-revisions',
        label: 'Pending Merit Revisions',
        section: PAYROLL_PEOPLE,
        permission: 'compensation:update',
        switch: 'merit_payroll_apply',
      },
      {
        to: '/admin/payroll/loans-advances',
        label: 'Loans & Advances',
        section: PAYROLL_PEOPLE,
        permission: 'payroll:read',
      },
      {
        to: '/admin/payroll/attendance-inputs',
        label: 'Attendance & Inputs',
        section: PAYROLL_PEOPLE,
        permission: 'payroll:read',
      },
      {
        to: '/admin/payroll/employee-pay/tax-declarations',
        label: 'Tax Declarations',
        section: PAYROLL_PEOPLE,
        permission: 'payroll.tax.own:manage',
      },
      {
        to: '/admin/payroll/operations/runs',
        label: 'Run & Review',
        section: PAYROLL_RUN,
        permission: 'payroll:read',
      },
      // Payments & Payslips and Full & Final (ADR 0015). Both are any-of: a manager reads their
      // team's payment STATUS, and the owner approves settlements without holding `fnf:execute`.
      {
        to: '/admin/payroll/operations/payments',
        label: 'Payments & Payslips',
        section: PAYROLL_RUN,
        permission: ['payroll.payment:read', 'payroll.payment.team:read', 'payslip:publish'],
      },
      {
        to: '/admin/payroll/operations/fnf',
        label: 'Full & Final (FnF)',
        section: PAYROLL_RUN,
        permission: ['fnf:read', 'fnf:execute', 'fnf:approve', 'fnf.clearance:manage'],
      },
      {
        to: '/admin/payroll/compliance/taxes',
        label: 'Compliance & Taxes',
        section: PAYROLL_COMPLIANCE,
        permission: 'statutory.own:read',
      },
      {
        to: '/admin/payroll/compliance/reports',
        label: 'Reports & Accounts',
        section: PAYROLL_COMPLIANCE,
        permission: ['payroll:read', 'payroll.report.team:read'],
      },
      {
        to: '/admin/payroll/compliance/salary-queries',
        label: 'Salary Queries',
        section: PAYROLL_COMPLIANCE,
        permission: 'payroll.query.own:manage',
      },
    ],
  },
  {
    to: '/admin/performance/insights',
    label: 'Performance',
    module: 'performance_okr',
    children: [
      { to: '/admin/performance', label: 'Goals Overview', permission: 'goal:manage' },
      {
        to: '/admin/performance/objectives',
        label: 'Company Objectives',
        permission: 'goal:manage',
      },
      {
        to: '/admin/performance/review-cycles',
        label: 'Review Cycles',
        permission: 'review.cycle:manage',
      },
      {
        // The guided set-up for a new cycle (details, then roster). Nothing else linked it.
        to: '/admin/performance/review-cycles/setup',
        label: 'New Cycle Setup',
        permission: 'review.cycle:manage',
      },
      {
        to: '/admin/performance/calibration',
        label: 'Calibration',
        permission: 'review.calibrate:execute',
      },
      {
        // HR's appeal queue (perf remediation T2.11, N17): every appeal across cycles, with the
        // reviews an upheld appeal reopened. Deciding happens on the review itself.
        to: '/admin/performance/appeals',
        label: 'Review Appeals',
        permission: 'review:appeal:read',
      },
      {
        to: '/admin/performance/competencies',
        label: 'Competencies',
        permission: 'competency:manage',
      },
      {
        to: '/admin/performance/review-templates',
        label: 'Review Templates',
        permission: 'competency:manage',
      },
      {
        to: '/admin/performance/goal-templates',
        label: 'Goal Templates',
        permission: 'goal:manage',
      },
      {
        to: '/admin/performance/insights',
        label: 'Insights',
        permission: 'analytics.performance:read',
      },
      {
        to: '/admin/performance/recognition/awards',
        label: 'Recognition Awards',
        permission: 'recognition:manage',
        module: 'recognition',
      },
      {
        to: '/admin/performance/recognition/points-allocation',
        label: 'Recognition Points',
        permission: 'recognition:manage',
        module: 'recognition',
      },
      {
        to: '/admin/performance/recognition/values',
        label: 'Recognition Values',
        permission: 'recognition:manage',
        module: 'recognition',
      },
      {
        to: '/admin/performance/recognition/rewards',
        label: 'Reward Catalogue',
        permission: 'recognition:manage',
        module: 'recognition',
      },
      {
        to: '/admin/performance/recognition/redemptions',
        label: 'Reward Redemptions',
        permission: 'recognition:manage',
        module: 'recognition',
      },
    ],
  },
  {
    to: '/admin/recruitment/requisitions',
    label: 'Hire',
    module: 'ats_recruitment',
    // No single permission gates the whole section: each subsection carries its OWN read-ish
    // permission (requisition:read for both Requisitions and Postings, candidate:read,
    // interview:read, offer:create — there's no offer:read — and recruitment.analytics:read), and
    // they are realistically held by different people (an interviewer is not a recruiter).
    children: [
      {
        to: '/admin/recruitment/requisitions',
        label: 'Requisitions',
        permission: 'requisition:read',
      },
      { to: '/admin/recruitment/postings', label: 'Postings', permission: 'requisition:read' },
      { to: '/admin/recruitment/candidates', label: 'Candidates', permission: 'candidate:read' },
      { to: '/admin/recruitment/interviews', label: 'Interviews', permission: 'interview:read' },
      { to: '/admin/recruitment/assessments', label: 'Assessments', permission: 'candidate:read' },
      { to: '/admin/recruitment/talent-pool', label: 'Talent Pool', permission: 'candidate:read' },
      // `offer:create` is the closest thing to a "read" permission offers has (see above).
      { to: '/admin/recruitment/offers', label: 'Offers', permission: 'offer:create' },
      // Accepted offers on the way to a first day, with background-check status.
      { to: '/admin/recruitment/joining', label: 'Joining', permission: 'offer:create' },
      {
        to: '/admin/recruitment/analytics',
        label: 'Analytics',
        permission: 'recruitment.analytics:read',
      },
      // Hiring flows, rejection reasons and candidate email templates.
      { to: '/admin/recruitment/setup', label: 'Hiring setup', permission: 'candidate:manage' },
    ],
  },
  {
    to: '/projects/all',
    label: 'Projects',
    module: 'projects',
    // DECISION (nav/ProjectsPage rework, revised): this is the ONLY place the "Administration"
    // tier (All Projects, Work Forms, Timesheet Policy, task configuration, KPIs, Employee
    // Targets) lives — it is not also duplicated into NAV's `/projects` entry, so a user with
    // admin rights sees each PM admin screen in exactly one sidebar, not two. "Team" tier
    // (Next-Day Planning, Timesheet/Task Approvals, Extension Requests, Resource Allocation)
    // and "Reports" tier (Analytics, Timesheet Register) stay exclusively in NAV's `/projects`
    // entry — they're day-to-day manager/approver screens and operational reports, not
    // configuration, so Admin > Projects is the wrong home for them.
    children: [
      {
        to: '/projects/all',
        label: 'All Projects',
        // Matches ProjectsPage's `canSeeAllProjects` exactly — MANAGER and above.
        permission: ['project:create', 'project:update'],
      },
      { to: '/projects/forms', label: 'Work Forms', permission: 'work_form:manage' },
      { to: '/projects/policy', label: 'Timesheet Policy', permission: 'org.settings:update' },
      {
        to: '/projects/task-statuses',
        label: 'Task Statuses',
        // Matches ProjectsPage's `canManageTaskStatuses` exactly (ADR 0020 §6).
        permission: 'org.settings:update',
      },
      {
        to: '/projects/task-types',
        label: 'Task Types',
        // Same permission as Task Statuses — both are `canManageTaskStatuses` in ProjectsPage.
        permission: 'org.settings:update',
      },
      {
        to: '/projects/task-templates',
        label: 'Task Templates',
        permission: 'org.settings:update',
      },
      {
        to: '/projects/task-reminders',
        label: 'Task Reminders',
        permission: 'org.settings:update',
      },
      { to: '/projects/kpis', label: 'KPIs', permission: 'org.settings:update' },
      {
        to: '/projects/employee-targets',
        label: 'Employee Targets',
        permission: 'org.settings:update',
      },
      // Analytics and Timesheet Register are NOT duplicated here — they're operational reports,
      // not configuration, and every role that can see them (manager and up) already reaches
      // them from NAV's `/projects` "Reports" section on the everyday Core Modules flyout.
    ],
  },
  {
    to: '/asset-management/dashboard',
    label: 'Assets',
    module: 'asset_management',
    showActiveDot: true,
    children: [
      { to: '/asset-management/dashboard', label: 'Dashboard', permission: 'asset:analytics:read' },
      {
        to: '/asset-management/operations',
        label: 'Assignments & Transfers',
        permission: 'asset:assign',
      },
      {
        to: '/asset-management/maintenance',
        label: 'Maintenance',
        permission: 'asset:maintenance:manage',
      },
      {
        // The shell root, not its first tab: NavLink prefix-matches this, so the row (and its
        // active dot) stays lit on every tab. The shell's index route redirects to Configuration.
        to: '/asset-management/administration',
        label: 'Administration',
        permission: [
          'asset.category:read',
          'asset.vendor:read',
          'asset.batch:read',
          'asset:audit:read',
        ],
        tabs: ASSET_ADMINISTRATION_TABS,
      },
    ],
  },
  {
    to: '/admin/helpdesk',
    // Not "Helpdesk": the employee menu's "Help desk" is where people raise tickets; this is the
    // agents' console.
    label: 'Help desk console',
    module: 'helpdesk',
    // No parent permission: an agent (ticket:manage, from team membership) sees the console only,
    // a Helpdesk Manager sees everything. Each child carries the check its route makes.
    children: [
      {
        to: '/admin/helpdesk',
        label: 'Ticket Console',
        permission: ['ticket:manage', 'ticket.all:manage'],
      },
      { to: '/admin/helpdesk/reports', label: 'Reports', permission: 'ticket.report:read' },
      {
        to: '/admin/helpdesk/categories',
        label: 'Categories',
        section: HELPDESK_SETTINGS,
        permission: 'ticket.config:manage',
      },
      {
        to: '/admin/helpdesk/teams',
        label: 'Teams',
        section: HELPDESK_SETTINGS,
        permission: 'ticket.config:manage',
      },
      {
        to: '/admin/helpdesk/sla',
        label: 'SLAs & Hours',
        section: HELPDESK_SETTINGS,
        permission: 'ticket.config:manage',
      },
    ],
  },
  {
    to: '/admin/engage/announcements',
    // Not plain "Engage", which is the employee menu's section of the same name.
    label: 'Manage Engage',
    module: 'engage',
    // Org-wide communication (ADR 0029, ADR 0027). Recognition's awards and points stay under
    // Performance, next to the rest of the performance programme.
    children: [
      {
        to: '/admin/engage/insights',
        label: 'Engagement Insights',
        permission: ['announcement:manage', 'survey:manage'],
      },
      {
        to: '/admin/engage/announcements',
        label: 'Announcements',
        permission: 'announcement:manage',
      },
      { to: '/admin/engage/surveys', label: 'Surveys & Polls', permission: 'survey:manage' },
      {
        to: '/admin/engage/surveys/templates',
        label: 'Survey Templates',
        permission: 'survey:manage',
      },
      {
        to: '/admin/engage/surveys/recurrences',
        label: 'Recurring Surveys',
        permission: 'survey:manage',
      },
    ],
  },
  {
    // The parent opens the Settings hub: every settings page, searchable, grouped by job.
    to: '/admin/settings',
    label: 'Settings',
    children: [
      {
        to: '/admin/settings',
        label: 'All settings',
        // Anyone who can open at least one settings page below.
        permission: [
          'role:read',
          'org.settings:read',
          'notification.config:read',
          'notification.delivery:read',
          'integration:read',
          'org.billing:read',
          'ai.settings:manage',
          'org.group:manage',
        ],
      },
      {
        to: '/admin/settings/roles',
        label: 'Roles & Permissions',
        permission: ['role:read', 'org.settings:read'],
      },
      // Company groups (ADR 0034). The page itself tells a non-owner holding the permission that it
      // is for the group's owner.
      { to: '/admin/settings/group-access', label: 'Group Access', permission: 'org.group:manage' },
      {
        to: '/admin/settings/approval-workflows',
        label: 'Approval Workflows',
        // Every employee holds `workflow:read` at SELF scope, so only an org-wide grant counts —
        // the same check the pages themselves make (workflowQueries.ts).
        permission: ['workflow:read', 'workflow:manage'],
        orgWide: true,
        module: 'custom_workflow',
      },
      {
        to: '/admin/settings/notifications',
        label: 'Notifications',
        permission: ['notification.config:read', 'notification.delivery:read'],
      },
      {
        to: '/admin/settings/integrations',
        label: 'Integrations',
        // Calendars, or connected applications (ADR 0033): either permission opens the page.
        permission: ['integration:read', 'app_integration:read'],
      },
      // B-P5 — reachable regardless of any module's own on/off state (§4.4: otherwise nobody could
      // ever turn one back on), so deliberately carries no `module` of its own.
      { to: '/admin/settings/modules', label: 'Modules', permission: 'org.billing:read' },
      // `ai.settings:manage` rather than `ai.copilot:use`: everyone can talk to the copilot, but the
      // allowance, usage-by-person and add-on requests are an org-level concern.
      {
        to: '/admin/ai',
        label: 'AI Copilot',
        permission: 'ai.settings:manage',
        module: 'ai_copilot',
      },
      { to: '/admin/audit', label: 'Audit Log', permission: 'audit:read' },
      { to: '/admin/compliance', label: 'Compliance', permission: 'compliance:read' },
      // CHT-14: where the `chat.message.dlp_flagged` notification actually leads — HR-admin tier
      // only (see `chat.dlp:review` in roles.ts), same tier as Audit Log and Compliance above.
      { to: '/admin/chat/dlp-review', label: 'Chat DLP Review', permission: 'chat.dlp:review' },
      // Owner-only (see `chat.import:execute` in roles.ts, same tier as `chat.export:execute`) —
      // bulk-brings historical chat from another platform into real conversations/messages rows.
      { to: '/admin/chat/import', label: 'Import Chat History', permission: 'chat.import:execute' },
      // HR-admin tier (see `chat.settings:manage` in roles.ts): org-wide edit window, link previews,
      // presence visibility and who may list archived channels.
      { to: '/admin/chat/settings', label: 'Chat Settings', permission: 'chat.settings:manage' },
    ],
  },
];

function checkPermission(
  actor: Actor,
  perm?: Permission | readonly Permission[],
  orgWide?: boolean,
): boolean {
  if (!perm) return true;
  const perms: readonly Permission[] = typeof perm === 'string' ? [perm] : perm;
  return perms.some((p) =>
    orgWide ? can(actor, p, { orgId: actor.orgId }) : canAnywhere(actor, p),
  );
}

/** B-P5: absent `module` means every module (org-level sections are never gated). `enabledModules`
 * undefined is treated as "unknown, show everything" rather than "hide everything" — a caller that
 * hasn't wired the session's module list through yet (or a test with no actor context) must not
 * silently blank the whole menu; only an EXPLICIT empty list hides a gated item. */
function switchOn(sw: NavSwitch | undefined, switches: NavSwitchFlags | undefined): boolean {
  // No switches passed (a caller that has not been taught about them, a pure unit test): only the
  // permission decides, which is how the menu behaved before switches existed.
  if (!sw || switches === undefined) return true;
  return switches[sw] === true;
}

function checkModule(
  module: ModuleKey | undefined,
  enabledModules: readonly ModuleKey[] | undefined,
): boolean {
  if (!module) return true;
  if (!enabledModules) return true;
  return enabledModules.includes(module);
}

export function visibleNav(
  actor: Actor,
  items: readonly NavItem[],
  enabledModules?: readonly ModuleKey[],
  switches?: NavSwitchFlags,
): NavItem[] {
  return (
    items
      .filter(
        (item) =>
          checkPermission(actor, item.permission, item.orgWide) &&
          checkModule(item.module, enabledModules) &&
          switchOn(item.switch, switches),
      )
      .map((item) => {
        const subItems = item.subItems?.filter((sub) => checkPermission(actor, sub.permission));
        if (!item.children) return { ...item, subItems };
        const children = item.children
          .filter(
            (child) =>
              checkPermission(actor, child.permission, child.orgWide) &&
              checkModule(child.module, enabledModules) &&
              switchOn(child.switch, switches),
          )
          .map((child) => ({
            ...child,
            subLinks: child.subLinks?.filter((s) => checkPermission(actor, s.permission)),
          }));
        return { ...item, subItems, children };
      })
      // A parent with its own `permission` was already filtered above; one WITHOUT one (Hire, Time,
      // Performance, ...: their pages carry heterogeneous permissions, so nothing gates the parent
      // itself) must not render as a section leading to an empty flyout for an actor who holds
      // none of its children's permissions. A section of only "soon" children still renders.
      .filter((item) => !item.children || item.children.length > 0)
  );
}

/**
 * Every link a menu can render, with its query string dropped — for the route-coverage test and
 * anything else that needs the full map of the menu. `soon` children are not links.
 */
export function menuLinks(items: readonly NavItem[]): string[] {
  const links: string[] = [];
  const add = (to: string) => links.push(to.split('?')[0] ?? to);
  for (const item of items) {
    add(item.to);
    for (const sub of item.subItems ?? []) add(`${item.to}/${sub.path}`);
    for (const child of item.children ?? []) {
      if (child.status === 'soon') continue;
      add(child.to);
      for (const link of child.subLinks ?? []) add(link.to);
      for (const tab of child.tabs ?? []) add(tab.to);
    }
  }
  return links;
}
